import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Compass, Brain, TrendingUp, Users, CheckCircle2, Lightbulb, ChevronDown } from 'lucide-react';
import LeadForm from '@/components/LeadForm';

const WA = 'https://wa.me/919930010800?text=Hi%20Planet%20Gyaan!%20I%27d%20like%20to%20book%20a%20career%20counselling%20session.';
const TEL = 'tel:+919930010800';
const LOGO = 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/no8w3qt3_Untitled%20design-73.png';

const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const painPoints = [
  { icon: Brain, title: 'Confused About Streams?', desc: 'Science, Commerce, or Arts? Engineering or Medicine? We help you weigh your options objectively.' },
  { icon: Users, title: 'Peer Pressure?', desc: "Don't choose a career because your friends did. We help you find YOUR path based on your strengths." },
  { icon: TrendingUp, title: 'Unclear About Opportunities?', desc: 'The career landscape is vast. We map out realistic paths, entrance exams, and college options for you.' },
  { icon: Lightbulb, title: 'Undiscovered Potential?', desc: 'Many students have talents they don\'t even know about. Our assessment helps uncover hidden strengths.' },
];

const steps = [
  { num: '01', title: 'Career Library Walkthrough', desc: 'Explore 1300+ career options through our comprehensive career library.', detail: 'Students get a guided walkthrough of our extensive career library covering traditional and emerging career paths across all industries. This opens their eyes to possibilities they never knew existed.' },
  { num: '02', title: '1:1 Discussion & Goal Setting', desc: 'Understand your approach, aspirations, and life goals in a personal session.', detail: 'Our counsellor has an in-depth 1:1 discussion with the student to understand their interests, strengths, academic performance, and long-term aspirations. Parents are welcome to join.' },
  { num: '03', title: 'Psychometric Assessment', desc: '5-dimensional psychometric test to scientifically identify strengths and interests.', detail: 'A comprehensive psychometric test measures aptitude, personality, interests, learning style, and emotional quotient. The detailed report reveals hidden strengths and natural inclinations.' },
  { num: '04', title: 'Career Shortlisting', desc: 'Narrow down to 25-30 relevant options and finalize top 3 best-fit careers.', detail: 'Based on test results and discussion, we identify 25-30 relevant career paths, then work with the student to shortlist the top 3 that align perfectly with their profile and goals.' },
  { num: '05', title: 'Complete Career Roadmap', desc: 'Detailed step-by-step roadmap for all 3 career options with skill development plan.', detail: 'You receive a comprehensive written roadmap for each of your top 3 careers - including recommended streams, entrance exams, top colleges, timeline, and a dedicated skills development plan for future readiness.' },
];

const benefits = [
  'Psychometric Test Report - Understand strengths & interests',
  '1:1 Expert Counselling Sessions - Personalized support',
  'Dedicated Career Roadmap - Step-by-step plan for 3 best-fit careers',
  'Skills Development Roadmap - Future-ready skill growth plan',
  'Access to 1300+ Career Options Library',
  'Complete Career Clarity - Guaranteed',
  'Parent consultation included',
];

const CareerCounselling = () => {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <>
      <Helmet>
        <title>Career Counselling for Students | Planet Gyaan | Santacruz Mumbai</title>
        <meta name="description" content="Expert 1:1 career counselling for CBSE students in Grades 8-12. Stream selection, JEE/NEET guidance, college roadmap. Planet Gyaan, Santacruz West, Mumbai. Call 9930010800." />
        <meta name="keywords" content="career counselling students, career guidance Mumbai, stream selection help, JEE NEET guidance, college counselling, Planet Gyaan Santacruz" />
        <meta property="og:title" content="Career Counselling - Planet Gyaan" />
        <meta property="og:description" content="Shape your future with expert 1:1 career guidance for Grades 8-12." />
        <meta property="og:image" content={LOGO} />
        <link rel="canonical" href="https://planetgyaan.com/career-counselling" />
      </Helmet>

      {/* HERO */}
      <section data-testid="career-hero" className="relative min-h-[85vh] bg-[#18181B] flex items-center overflow-hidden noise-bg">
        <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="inline-block text-[#F2C402] font-bold uppercase tracking-[0.2em] text-xs md:text-sm mb-6 font-body"
              >
                1:1 Career Guidance
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
                className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]"
              >
                <span className="text-white">Shape Your</span><br/>
                <span className="text-[#F2C402]">Future.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
                className="text-white/60 text-base md:text-lg mt-6 max-w-md font-body leading-relaxed"
              >
                Expert 1:1 career counselling for students in Grades 8-12. Make informed decisions about your academic and professional future.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
                className="flex flex-wrap gap-4 mt-8"
              >
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="career-whatsapp-cta"
                  className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 transition-all">
                  <WaIcon /> Book a Session
                </a>
                <a href={TEL} data-testid="career-call-cta"
                  className="flex items-center gap-2 bg-[#F2C402] text-[#0A0A0A] px-6 py-3.5 rounded-xl font-bold text-sm hover:bg-[#E9811F] hover:text-white transition-all">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </motion.div>
            </div>
            {/* Interactive career path visualization */}
            <motion.div
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex flex-col gap-4"
            >
              {[
                { path: 'Engineering', exams: 'JEE Main / Advanced', icon: '\u2699\uFE0F' },
                { path: 'Medical', exams: 'NEET UG', icon: '\uD83E\uDE7A' },
                { path: 'Research', exams: 'KVPY / Olympiads', icon: '\uD83D\uDD2C' },
                { path: 'Design & Architecture', exams: 'NATA / JEE Paper 2', icon: '\uD83C\uDFE8' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + i * 0.15, duration: 0.5 }}
                  whileHover={{ scale: 1.03, x: -5 }}
                  className="glass-card rounded-xl p-5 cursor-pointer group flex items-center gap-4"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p className="text-white font-sans font-bold text-sm group-hover:text-[#F2C402] transition-colors">{item.path}</p>
                    <p className="text-white/40 text-xs font-body mt-0.5">{item.exams}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/20 ml-auto group-hover:text-[#F2C402] group-hover:translate-x-1 transition-all" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CAREER GUIDANCE */}
      <section data-testid="career-why" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#F2C402] font-bold uppercase tracking-[0.15em] text-xs font-body">Why It Matters</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              Don't Leave Your <span className="text-[#F2C402]">Future to Chance</span>
            </h2>
            <p className="text-[#52525B] text-base md:text-lg mt-4 font-body max-w-xl mx-auto">
              The decisions you make in school shape your entire career. Get expert guidance before it's too late.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5">
            {painPoints.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className="brutalist-card rounded-2xl p-6 md:p-8 bg-white"
              >
                <div className="w-10 h-10 rounded-lg bg-[#F2C402]/10 flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-[#F2C402]" />
                </div>
                <h3 className="font-sans font-bold text-[#121212] text-base md:text-lg mb-2">{p.title}</h3>
                <p className="text-[#52525B] text-sm font-body leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE PROCESS (Light BG, Click to Expand) */}
      <section data-testid="career-process" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#F2C402] font-bold uppercase tracking-[0.15em] text-xs font-body">Our Process</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight mt-3">
              How It <span className="text-[#F2C402]">Works</span>
            </h2>
            <p className="text-[#52525B] text-sm font-body mt-3">Click each step to learn more</p>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                data-testid={`career-step-${i}`}
                onClick={() => setActiveStep(activeStep === i ? null : i)}
                className={`rounded-2xl p-6 cursor-pointer transition-all border-2 ${
                  activeStep === i
                    ? 'border-[#F2C402] bg-white shadow-[4px_4px_0px_0px_rgba(242,196,2,0.5)]'
                    : 'border-[#121212] bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-[1px] hover:-translate-y-[1px]'
                }`}
              >
                <div className="flex items-start gap-4">
                  <span className={`text-3xl font-black font-sans shrink-0 transition-colors ${
                    activeStep === i ? 'text-[#F2C402]' : 'text-[#F2C402]/30'
                  }`}>{s.num}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans font-bold text-[#121212] text-base md:text-lg">{s.title}</h3>
                      <ChevronDown className={`w-5 h-5 text-[#52525B] transition-transform ${activeStep === i ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-[#52525B] text-sm font-body leading-relaxed mt-1">{s.desc}</p>
                    <AnimatePresence>
                      {activeStep === i && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden mt-3 pt-3 border-t border-[#F2C402]/20 text-[#121212] text-sm font-body leading-relaxed"
                        >
                          {s.detail}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                {i < steps.length - 1 && activeStep !== i && (
                  <div className="ml-5 mt-2 w-0.5 h-4 bg-[#F2C402]/20" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section data-testid="career-benefits" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-16 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
              What You <span className="text-[#F2C402]">Get</span>
            </h2>
          </motion.div>
          <div className="space-y-4">
            {benefits.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
                className="flex items-start gap-3 bg-[#FAFAFA] rounded-xl p-4 border-2 border-[#121212] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
              >
                <CheckCircle2 className="w-5 h-5 text-[#F2C402] shrink-0 mt-0.5" />
                <span className="text-[#121212] text-sm font-body font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK BANNER */}
    <section data-testid="book-banner" className="py-12 bg-white">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-2xl overflow-hidden border-2 border-[#121212] shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/1vqzj2po_ChatGPT%20Image%20Apr%2030%2C%202026%20at%2004_43_48%20PM.png"
            alt="UNSTUCK - Book by Aravind Achari - Get Out of Your Own Way, Starting Now"
            className="w-full h-auto"
            data-testid="unstuck-book-banner"
          />
        </motion.div>
      </div>
    </section>

    {/* CTA + FORM */}
      <section data-testid="career-cta" className="py-16 md:py-24 bg-[#F2C402] border-y-2 border-[#121212]">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight">
                Your Future Starts Now
              </h2>
              <p className="text-[#0A0A0A]/70 text-base md:text-lg mt-4 font-body max-w-lg">
                Book your 1:1 career counselling session. Contact us today.
              </p>
              <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="career-cta-whatsapp"
                  className="flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#121212] transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]">
                  <WaIcon /> WhatsApp Us
                </a>
                <a href={TEL} data-testid="career-cta-call"
                  className="flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#FAFAFA] transition-all border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
              <Link to="/" data-testid="career-back-home" className="inline-flex items-center gap-1 text-[#0A0A0A]/60 text-sm font-body mt-6 hover:text-[#0A0A0A] transition-colors">
                <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
              </Link>
            </div>
            <LeadForm />
          </div>
        </div>
      </section>
    </>
  );
};

export default CareerCounselling;
